import { useCallback, useEffect, useState } from 'react';
import { membersApi } from '../lib/api';

// Loads workspace members and exposes manage actions (add/role/remove).
export default function useMembers(workspaceId) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMembers = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const data = await membersApi.list(workspaceId, signal);
        setMembers(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchMembers(controller.signal);
    return () => controller.abort();
  }, [fetchMembers]);

  const addMember = useCallback(
    async (email, role) => {
      await membersApi.add(workspaceId, email, role);
      await fetchMembers();
    },
    [workspaceId, fetchMembers]
  );

  const updateRole = useCallback(
    async (userId, role) => {
      await membersApi.updateRole(workspaceId, userId, role);
      await fetchMembers();
    },
    [workspaceId, fetchMembers]
  );

  const removeMember = useCallback(
    async (userId) => {
      await membersApi.remove(workspaceId, userId);
      await fetchMembers();
    },
    [workspaceId, fetchMembers]
  );

  return { members, loading, error, addMember, updateRole, removeMember };
}
